import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { ActivationFilter } from "@bfc-software/bfc-web-ui/lib/task-plan-activation";

/** Re-exported so this tree's one filter module stays its one import. */
export { ActivationFilter };

/**
 * The filter bar the layout draws and both tabs read, as URL state.
 *
 * In the URL rather than in context, because every one of these narrows what is
 * on screen and a view a manager cannot send to someone else is a view they have
 * to describe over the phone. It also puts the filters where the two tabs both
 * already look: the layout owns the params, and each tab reads them through the
 * same hook whichever one is mounted.
 *
 * Each filter is an enumerated type rather than a string with reserved values.
 * A `SELECTOR_ALL` sentinel made "any selector" a value the predicate had to
 * remember to exclude, and forgetting is a filter that silently matches nothing;
 * `{ kind: "any" }` makes the compiler ask instead. The URL still carries
 * strings, so the validators below are the only place the two meet.
 */

const routeApi = getRouteApi(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout/tasks/_layout",
);

/** Free text matched as a substring, or nothing typed. */
export type TextFilter = { kind: "none" } | { kind: "query"; text: string };

/**
 * A selector, or anybody.
 *
 * Two cases rather than a nullable name, so "any selector" is a value the
 * compiler makes every reader handle rather than a null each one has to
 * remember to exclude.
 *
 * There is no "nobody" arm. Asking for the tasks nobody holds is asking for a
 * task *status*, and it is one - the `Unassigned` chip, which sends
 * `status=pending` (see `SCOPE_STATUSES` in `use-tasks.ts`). Having it here as
 * well made one question two controls, and the copy of it in this box was the
 * one no endpoint could answer for a plan card.
 */
export type SelectorFilter = { kind: "any" } | { kind: "named"; name: string };

/**
 * Some zones, or every zone. Ext ids are opaque strings here: they are another
 * system's identifiers, so the app assumes no data type for them. Numbers
 * exist only in the request builders (`toWireExtIds`). Ext ids rather than
 * names, so nothing on the request path waits on the item service (§9.2).
 * Enumerated for the reason this file states at :16-20: an optional array makes
 * "every zone" an empty-array sentinel every reader has to remember to exclude.
 */
export type ZoneFilter = { kind: "any" } | { kind: "some"; extIds: string[] };

/** As `ZoneFilter`; `CustomerRec.id`, not `location_id` (display only). */
export type CustomerFilter =
  { kind: "any" } | { kind: "some"; extIds: string[] };

export interface TaskFilters {
  task: TextFilter;
  route: TextFilter;
  selector: SelectorFilter;
  zone: ZoneFilter;
  customer: CustomerFilter;
}

/**
 * The plans tab's state filter.
 *
 * The vocabulary is shared (`lib/task-plan-activation`), because the internal
 * task plans page carries the same `activation` param and the router registers
 * one search space; what this tab owns is which members it offers, in what order,
 * and which one an absent param means.
 *
 * Five of the six, and `ALL` among them as a real member rather than the absence
 * of one.
 * It used to be a sentinel the route held (`ACTIVATION_ALL`) precisely because it
 * was also the default, and absent-means-default made the two indistinguishable;
 * now that the tab opens on `NOT_ACTIVATED` instead, "every plan" is a narrowing
 * a manager chose and has to be able to send to someone else - so it is carried,
 * and the enum can be the whole vocabulary with no reserved value beside it.
 *
 * The member left out is `ACTIVATED`, which is "released, whatever the floor has
 * done with it" - the union of the three chips this tab draws instead. A tab
 * whose subject is the release decision wants those three apart, and each is one
 * `fulfillmentState` value, so each is a chip. The internal plan inventory offers
 * the union rather than the parts, which is why the member exists at all.
 *
 * Which request each one makes is `activationQuery` in `use-plans.ts`.
 *
 * Validated here, with the searches, rather than on the plans route that uses it.
 * The layout owns this route tree's search schema and `setSearch` below rebuilds
 * the params from that schema on every keystroke - a param validated only by the
 * leaf would be dropped the next time someone typed in a search box.
 */

/**
 * What the tab opens on, and what an absent `activation` param means.
 *
 * The plans awaiting release, not every plan. The tab's subject is the release
 * decision, and a manager arriving at it is asking "what do I still have to put
 * on the floor" - a default that answered with the finished waves as well is one
 * they have to narrow before they can start. The other three chips are one click
 * away and each carries its own param, so nothing is hidden, only unranked.
 */
export const DEFAULT_ACTIVATION_FILTER: ActivationFilter =
  ActivationFilter.NOT_ACTIVATED;

/** Chip order, left to right. Exported so the route does not restate it. */
export const ACTIVATION_FILTERS: ReadonlyArray<ActivationFilter> = [
  ActivationFilter.NOT_ACTIVATED,
  ActivationFilter.NOT_STARTED,
  ActivationFilter.IN_PROGRESS,
  ActivationFilter.COMPLETED,
  ActivationFilter.ALL,
];

/**
 * Doubles as the chip row's key narrowing: the default chip and anything a
 * hand-edited URL carries both come back as `undefined`, which is the default.
 */
export function validateActivationFilter(
  raw: unknown,
): ActivationFilter | undefined {
  return ACTIVATION_FILTERS.includes(raw as ActivationFilter) &&
    raw !== DEFAULT_ACTIVATION_FILTER
    ? (raw as ActivationFilter)
    : undefined;
}

/**
 * The plans-tab twin of {@link resolveScope}, and the same contradiction.
 *
 * A plan awaiting activation has released nothing, so it holds no assignment to
 * anybody - and the selector narrows the plan listing to the plans holding a
 * live assignment to one named person. The two together describe a plan that
 * cannot exist, and the tab answers with an empty card list that reads as "no
 * plans left to release".
 *
 * The awkward half is that `Awaiting Activation` is this tab's *default*, so the
 * state to catch is an absent param rather than a present one:
 * `validateActivationFilter` has already collapsed both "no param" and an
 * explicit `not-activated` to `undefined` by the time this sees them. The
 * fallback therefore has to write `activation=all` into a URL that carried no
 * activation param at all, which is the one case where resolving a conflict adds
 * a param instead of changing one.
 *
 * As on the other tab, the selector is the half that survives, and clicking the
 * `Awaiting Activation` chip is not this case - that gesture drops the selector
 * in the same navigation.
 */
function resolveActivation(
  activation: ActivationFilter | undefined,
  selector: string | undefined,
): ActivationFilter | undefined {
  return activation === undefined && selector
    ? ActivationFilter.ALL
    : activation;
}

/**
 * The All tasks tab's status chips.
 *
 * In the URL for the same reason the plans tab's activation chip is: the chip is
 * a *request* - `status` on the activation listing (see `statusQuery` in
 * `use-tasks.ts`) - so the rows on screen are what the service returned, and a
 * chip held in `useState` would be a view that survives neither a reload nor a
 * paste.
 *
 * `open` is the tab's default and therefore the absence of a param, which keeps
 * the default view's URL clean; every other value, `all` included, is a real
 * narrowing that has to be carried.
 *
 * Validated here rather than on the `all` route for the same reason
 * `ActivationFilter` is: the layout owns this tree's search schema and
 * `setSearch` below rebuilds the params from it on every keystroke, so a param
 * validated only by the leaf would be dropped the next time someone typed.
 */
export enum TaskScope {
  /** Everything released and unfinished. The tab's default. */
  OPEN = "open",
  PROGRESS = "progress",
  UNASSIGNED = "unassigned",
  COMPLETED = "completed",
  ALL = "all",
}

/** What the tab opens on, and what an absent `scope` param means. */
export const DEFAULT_TASK_SCOPE: TaskScope = TaskScope.OPEN;

/** Chip order, left to right. Exported so the route does not restate it. */
export const TASK_SCOPES: ReadonlyArray<TaskScope> = [
  TaskScope.OPEN,
  TaskScope.PROGRESS,
  TaskScope.UNASSIGNED,
  TaskScope.COMPLETED,
  TaskScope.ALL,
];

/**
 * Doubles as the chip row's key narrowing: the default chip and anything a
 * hand-edited URL carries both come back as `undefined`, which is the default.
 */
export function validateTaskScope(raw: unknown): TaskScope | undefined {
  return TASK_SCOPES.includes(raw as TaskScope) && raw !== DEFAULT_TASK_SCOPE
    ? (raw as TaskScope)
    : undefined;
}

/**
 * The one pairing of chip and selector that can never hold a row.
 *
 * `Unassigned` is `status=pending`, which is precisely the work nobody holds,
 * and the selector box narrows to the work one named person holds. Asked
 * together they are a contradiction rather than a narrow search - the service
 * answers honestly with nothing, and an empty table under two controls that each
 * look reasonable is the kind of empty a manager reads as "no work" instead of
 * as "impossible question".
 *
 * Resolved rather than rejected: the chip moves to `All Statuses`, which is the
 * nearest thing that does answer, and the selector - the more specific of the
 * two, and the one someone went to a dropdown for - is what survives. Applied in
 * `validateTaskFiltersSearch`, so it holds for a pasted URL and for every
 * `setSearch` alike rather than only wherever a caller remembered.
 *
 * Clicking the `Unassigned` chip itself is not this case: that gesture drops the
 * selector in the same navigation (see the All tasks tab's `onSelect`), so what
 * arrives here has no selector to conflict with and the chip stands.
 */
function resolveScope(
  scope: TaskScope | undefined,
  selector: string | undefined,
): TaskScope | undefined {
  return scope === TaskScope.UNASSIGNED && selector ? TaskScope.ALL : scope;
}

/** Nothing narrowed. What the screen reads with no search params at all. */
export const EMPTY_FILTERS: TaskFilters = {
  task: { kind: "none" },
  route: { kind: "none" },
  selector: { kind: "any" },
  zone: { kind: "any" },
  customer: { kind: "any" },
};

/**
 * The filters as the URL carries them.
 *
 * Absent means unfiltered, so the default view's URL stays clean and a
 * hand-edited param that means nothing degrades to unfiltered rather than to an
 * empty screen.
 */
export interface TaskFiltersSearch {
  taskQuery?: string;
  routeQuery?: string;
  selector?: string;
  /**
   * Zone ext ids, comma-joined - `?zones=41,58`. A string and not `string[]`,
   * mirroring `items.tsx`'s `attrs` (`items.tsx:87-90,121-130`): TanStack's
   * default codec JSON-stringifies a real array, so `zones?: string[]` reads on
   * the wire as `?zones=%5B%2241%22%2C%2258%22%5D` - a param nobody can
   * hand-edit.
   */
  zones?: string;
  /** Customer ext ids, comma-joined - `?customers=1,3`. A string for the reason `zones` is. */
  customers?: string;
  /** Plans tab only; see `ActivationFilter`. */
  activation?: ActivationFilter;
  /** All tasks tab only; see `TaskScope`. */
  scope?: TaskScope;
  /**
   * The open task detail drawer's plan, and the paired {@link drawerTaskId}.
   *
   * Declared here rather than on either tab's leaf route for the same reason
   * `activation`/`scope` are: `setSearch` rebuilds every param on this schema on
   * every keystroke, and both tabs open the same drawer, so a param either leaf
   * owned alone would vanish the moment someone typed in a search box (see
   * `useTaskFilters`).
   */
  drawerPlanId?: string;
  /**
   * The open task detail drawer's task number, and the paired
   * {@link drawerPlanId}.
   *
   * Two params rather than one composite id: `GetTaskDetails` and
   * `ListTaskRequirements` both key on the plan's ULID *and* the task's
   * warehouse-scoped number, so a URL naming only one of them names half a
   * task - see `validateTaskDrawer`, which is exactly why a lone param there
   * is dropped rather than kept.
   */
  drawerTaskId?: number;
}

/**
 * Whitespace-only is nothing typed, not a query that matches everything.
 *
 * A number is accepted as well as a string, because every one of these queries is
 * an id a person types by hand. TanStack quotes numeric-looking strings on the
 * way out (`?routeQuery=%22204%22`) so they survive the round trip, but a manager
 * hand-editing or retyping the URL writes `?routeQuery=204` - which parses back
 * as a number, and a string-only check drops it without saying so.
 */
function validateText(raw: unknown): string | undefined {
  if (typeof raw === "number") {
    return Number.isFinite(raw) ? String(raw) : undefined;
  }

  if (typeof raw !== "string") {
    return undefined;
  }

  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * The one place `?zones=41,58` / `?customers=1,3` becomes ids. A token is what is
 * left after trimming; empty tokens are dropped and nothing else is checked,
 * because what an id looks like is the owning service's business. De-duplicated
 * and sorted (default code-unit order) so one selection is one URL and one query
 * key. A bare number is accepted for the reason `validateText` accepts one.
 */
function parseExtIds(raw: unknown): string[] {
  const text =
    typeof raw === "number" ? String(raw) : typeof raw === "string" ? raw : "";
  const tokens = text
    .split(",")
    .map((part) => part.trim())
    .filter((token) => token.length > 0);
  return [...new Set(tokens)].sort();
}

/** The param as it is kept: canonical, or absent when it names nothing. */
function validateExtIds(raw: unknown): string | undefined {
  const ids = parseExtIds(raw);
  return ids.length > 0 ? ids.join(",") : undefined;
}

/**
 * The wire edge: the only place an ext id becomes a number. `customerExtIds` and
 * `zoneExtIds` are `int64[]`, so a token that is not a safe integer cannot be sent
 * and is dropped here rather than as `NaN`. Lives in this file, beside the ADTs
 * whose `extIds` it converts; both request-builder files already import from it.
 */
export function toWireExtIds(ids: readonly string[]): number[] {
  // `Number("")` is `0`, a safe integer, so a blank token is dropped first.
  return ids
    .filter((id) => id.trim().length > 0)
    .map(Number)
    .filter(Number.isSafeInteger);
}

/**
 * The drawer's identity, read as one answer rather than two independent params.
 *
 * A hand-edited or stale URL can carry a `drawerTaskId` with no `drawerPlanId`
 * (or a mangled one) just as easily as it can carry neither - and a drawer that
 * opened on half an identity would build a request with `planId: undefined`
 * rather than fail to open at all. So this validates the pair together: either
 * both survive, or the drawer reads as closed, which is the same fallback a
 * task nobody can find degrades to (see `useTaskDetail`'s 404 handling).
 */
function validateTaskDrawer(
  search: Record<string, unknown> | undefined,
): Pick<TaskFiltersSearch, "drawerPlanId" | "drawerTaskId"> {
  const planId = search?.["drawerPlanId"];
  const taskId = search?.["drawerTaskId"];

  if (typeof planId !== "string" || planId.trim().length === 0) {
    return {};
  }

  // Accepted as well as a string for the same reason `validateText` accepts a
  // number: TanStack round-trips a numeric param as a number, but a manager
  // retyping the URL by hand writes a bare digit string.
  const taskNumber = typeof taskId === "number" ? taskId : Number(taskId);
  if (!Number.isInteger(taskNumber) || taskNumber <= 0) {
    return {};
  }

  return { drawerPlanId: planId, drawerTaskId: taskNumber };
}

export function validateTaskFiltersSearch(
  search?: Record<string, unknown>,
): TaskFiltersSearch {
  const taskQuery = validateText(search?.["taskQuery"]);
  const routeQuery = validateText(search?.["routeQuery"]);
  const selector = validateText(search?.["selector"]);
  const zones = validateExtIds(search?.["zones"]);
  const customers = validateExtIds(search?.["customers"]);
  const activation = resolveActivation(
    validateActivationFilter(search?.["activation"]),
    selector,
  );
  const scope = resolveScope(validateTaskScope(search?.["scope"]), selector);
  const drawer = validateTaskDrawer(search);

  return {
    ...(taskQuery ? { taskQuery } : {}),
    ...(routeQuery ? { routeQuery } : {}),
    ...(selector ? { selector } : {}),
    ...(zones ? { zones } : {}),
    ...(customers ? { customers } : {}),
    ...(activation ? { activation } : {}),
    ...(scope ? { scope } : {}),
    ...drawer,
  };
}

/** The URL's strings, read as the enumerated filters everything else uses. */
export function toFilters(search: TaskFiltersSearch): TaskFilters {
  return {
    task: search.taskQuery
      ? { kind: "query", text: search.taskQuery }
      : { kind: "none" },
    route: search.routeQuery
      ? { kind: "query", text: search.routeQuery }
      : { kind: "none" },
    selector: search.selector
      ? { kind: "named", name: search.selector }
      : { kind: "any" },
    zone: search.zones
      ? { kind: "some", extIds: parseExtIds(search.zones) }
      : { kind: "any" },
    customer: search.customers
      ? { kind: "some", extIds: parseExtIds(search.customers) }
      : { kind: "any" },
  };
}

/**
 * A filter written back as its param.
 *
 * `undefined` rather than an empty string for the unfiltered cases: TanStack
 * drops undefined params entirely, which is what keeps clearing a filter and
 * never setting one at the same URL.
 */
export function selectorParam(filter: SelectorFilter): string | undefined {
  switch (filter.kind) {
    case "any":
      return undefined;
    case "named":
      return filter.name;
  }
}

export function textParam(filter: TextFilter): string | undefined {
  return filter.kind === "query" ? filter.text : undefined;
}

export function zonesParam(filter: ZoneFilter): string | undefined {
  switch (filter.kind) {
    case "any":
      return undefined;
    case "some":
      return filter.extIds.length > 0 ? filter.extIds.join(",") : undefined;
  }
}

export function customersParam(filter: CustomerFilter): string | undefined {
  switch (filter.kind) {
    case "any":
      return undefined;
    case "some":
      return filter.extIds.length > 0 ? filter.extIds.join(",") : undefined;
  }
}

/**
 * The filters, and the one way to change them.
 *
 * `setSearch` navigates with `replace`, so typing in a search box does not fill
 * the back button with a keystroke per character - the tab change before it
 * stays one press away.
 *
 * The read is bound to the layout route, because that is what owns the params.
 * The *write* deliberately is not: `routeApi.useNavigate()` binds `from` to the
 * layout's own `fullPath` (`…/tasks`), and a navigation with no `to` goes to
 * `from` - so every keystroke left the tab for `/tasks`, which redirects back to
 * `/tasks/plans` and drops the search on the way. Nothing moved, which is what a
 * filter bar that silently did nothing looked like. An unbound `useNavigate`
 * resolves against the location as it stands, so the params land on whichever
 * tab is actually open.
 *
 * Rebuilding the params from this schema is also what drops the All tasks tab's
 * `cursor`/`dir`, which the leaf route owns: a cursor names a boundary in the
 * result set that produced it, so it addresses nothing once a filter changes.
 * Narrowing returns to page 1, and it does so by omission rather than by any
 * caller remembering to clear it.
 */
export function useTaskFilters(): {
  filters: TaskFilters;
  search: TaskFiltersSearch;
  setSearch: (patch: TaskFiltersSearch) => void;
} {
  const search = routeApi.useSearch();
  const navigate = useNavigate();

  return {
    filters: toFilters(search),
    search,
    setSearch: (patch) => {
      void navigate({
        to: ".",
        search: (previous: TaskFiltersSearch) =>
          validateTaskFiltersSearch({ ...previous, ...patch }),
        replace: true,
      });
    },
  };
}

/** The task detail drawer's identity, once `validateTaskDrawer` has agreed both halves are present. */
export interface TaskDrawerIdentity {
  planId: string;
  taskId: number;
}

/** `search` read as the drawer's identity, or nothing open. */
export function taskDrawerIdentity(
  search: TaskFiltersSearch,
): TaskDrawerIdentity | null {
  return search.drawerPlanId !== undefined && search.drawerTaskId !== undefined
    ? { planId: search.drawerPlanId, taskId: search.drawerTaskId }
    : null;
}

/**
 * Opening and closing the drawer, as the two params both tabs write.
 *
 * A thin wrapper over `setSearch` rather than each tab building the patch
 * itself: `close` has to clear both params by the same omission rule
 * `selectorParam` uses elsewhere (`undefined` is what TanStack drops), and a
 * tab that set only one of them on open would hand `validateTaskDrawer` a URL
 * it treats as never having been opened at all.
 */
export function useTaskDetailDrawer(): {
  identity: TaskDrawerIdentity | null;
  open: (identity: TaskDrawerIdentity) => void;
  close: () => void;
} {
  const { search, setSearch } = useTaskFilters();

  return {
    identity: taskDrawerIdentity(search),
    open: ({ planId, taskId }) =>
      setSearch({ drawerPlanId: planId, drawerTaskId: taskId }),
    close: () =>
      setSearch({ drawerPlanId: undefined, drawerTaskId: undefined }),
  };
}
